"use client";

import { Network } from "@aptos-labs/ts-sdk";
import {
  AdapterNotDetectedWallet,
  AdapterWallet,
  DEFAULT_WALLET_CONNECTION_FALLBACKS,
  groupAndSortWallets,
  isMobile,
} from "@aptos-labs/wallet-adapter-react";
import {
  IconCheck,
  IconCheckCircle,
  IconDesktopFill,
  IconGift,
  IconXCircle,
} from "@decibeltrade/react-icons";
import type { ReferralCodeValidation } from "@decibeltrade/sdk";
import { IconButton, Spinner } from "@decibeltrade/web-core/components";
import { ModalContent, ModalRenderProps, openModal } from "@decibeltrade/web-core/modal";
import type { UseQueryResult } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { css, cx } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { Input } from "@/components/form/input";
import { Tabs } from "@/components/tabs";
import { toast } from "@/components/toast";
import { Tooltip } from "@/components/tooltip";
import { LAST_EVM_CHAIN_KEY, LAST_WALLET_TAB_KEY } from "@/constants/local-storage-keys";
import { readDexSdk } from "@/contexts/dex-sdk";
import { useSubmitInvitationCode } from "@/contexts/mutations/referral/use-submit-invitation-code";
import { useIsValidRefCodeQuery } from "@/contexts/queries/referral/use-is-valid-refcode";
import { env } from "@/env";
import { useLocalStorage } from "@/hooks/storage/use-local-storage";
import { useDebounce } from "@/hooks/use-debounce";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import {
  removePendingReferralCode,
  setPendingReferralCode,
} from "@/hooks/use-pending-referral-code";
import { useSwitchEvmNetwork } from "@/hooks/use-switch-evm-network";
import { getErrorMessage } from "@/utils/error";
import { openInNewTab } from "@/utils/open-in-new-tab";
import { REFERRAL_CODE_MAX_LENGTH } from "@/utils/referral-code";

import { Clickable } from "../clickable";
import { EVM_CHAINS, EvmChainId, EVMChainSelectorMenu } from "../evm-chain-selector-menu";
import { RadioTabGroup } from "../radio-tab-group";
import { PetraLogo } from "../vectors/petra-logo";
import { KeylessButton, KeylessButtonLayout } from "../wallet-selector/keyless-button";
import { WalletTabContent } from "../wallet-selector/wallet-tab-content";
import { WalletConnectEvmRow, WalletConnectSolanaRow } from "../wallet-selector/walletconnect-row";
import { ModalCloseButton } from "./modal-close-button";
import { openScanQrCodeModal } from "./scan-qr-code-modal";

export function openWalletSelectorModal() {
  openModal({
    id: "wallet-selector-modal",
    renderContent: (modalProps) => <WalletSelectorModal {...modalProps} />,
  });
}

export type Wallet = AdapterWallet | AdapterNotDetectedWallet;

const getDefaultTab = (
  evmWallets: Wallet[],
  solanaWallets: Wallet[],
  suiWallets: Wallet[],
  aptosWallets: Wallet[],
): SupportedNetworkId => {
  const hasSolanaWallet = solanaWallets.length > 0;
  const hasSuiWallet = suiWallets.length > 0;
  const hasEvmWallet = evmWallets.length > 0;
  const hasAptosWallet = aptosWallets.length > 0;

  // Note: since some wallets exist across networks, our priority should be the following
  // solana > ethereum > aptos > sui
  // if user has no wallets, default to solana
  return hasSolanaWallet
    ? "solana"
    : hasEvmWallet
      ? "ethereum"
      : hasAptosWallet
        ? "aptos"
        : hasSuiWallet
          ? "sui"
          : "solana";
};

const allSupportedNetworks = [
  {
    id: "aptos",
    name: "Aptos",
  },
  {
    id: "solana",
    name: "Solana",
  },
  {
    id: "ethereum",
    name: "EVM",
  },
  {
    id: "sui",
    name: "Sui",
  },
] as const;
export type SupportedNetworkId = (typeof allSupportedNetworks)[number]["id"];

const supportedNetworks = allSupportedNetworks;

const isSupportedNetworkId = (value: unknown): value is SupportedNetworkId =>
  typeof value === "string" && supportedNetworks.some((network) => network.id === value);

const isEvmChainId = (value: unknown): value is EvmChainId =>
  typeof value === "string" && EVM_CHAINS.some((chain) => chain.id === value);

function WalletSelectorModal({ close }: ModalRenderProps) {
  const { wallets, notDetectedWallets, account, hiddenWallets } = useDecibelWallet();
  const t = useTranslations("walletSelector");
  // Keyless sign-in isn't wired up on local/docker deploys.
  const isCustomNetwork = readDexSdk.config.network === Network.CUSTOM;

  const { availableWalletsWithFallbacks, availableWallets, installableWallets } =
    groupAndSortWallets([...wallets, ...notDetectedWallets], {
      fallbacks: {
        connections: DEFAULT_WALLET_CONNECTION_FALLBACKS,
        additionalFallbackWallets: hiddenWallets,
      },
    });

  const [lastUsedTab, setLastUsedTab] = useLocalStorage<SupportedNetworkId | null>(
    LAST_WALLET_TAB_KEY,
    null,
  );
  const [lastUsedEvmChain, setLastUsedEvmChain] = useLocalStorage<EvmChainId | null>(
    LAST_EVM_CHAIN_KEY,
    null,
  );

  const [selectedEvmChain, setSelectedEvmChain] = useState<EvmChainId>(() =>
    isEvmChainId(lastUsedEvmChain) ? lastUsedEvmChain : "arbitrum",
  );
  const selectedEvmChainConfig = EVM_CHAINS.find((chain) => chain.id === selectedEvmChain);
  const hasAttemptedNetworkSwitch = useRef(false);

  const { switchNetwork: handleEvmNetworkSwitch } = useSwitchEvmNetwork();

  useEffect(() => {
    if (!account || !selectedEvmChainConfig) {
      hasAttemptedNetworkSwitch.current = false;
      return;
    }

    // Only attempt network switch once per connection
    if (hasAttemptedNetworkSwitch.current) {
      return;
    }
    hasAttemptedNetworkSwitch.current = true;

    const handleConnection = async () => {
      const success = await handleEvmNetworkSwitch(selectedEvmChainConfig);
      if (success) {
        setLastUsedTab("ethereum");
        close();
      }
    };

    void handleConnection();
  }, [account, close, handleEvmNetworkSwitch, selectedEvmChainConfig, setLastUsedTab]);

  const { evmWallets, solanaWallets, suiWallets, aptosWallets } = [
    ...availableWallets,
    ...availableWalletsWithFallbacks,
  ].reduce<{
    evmWallets: Wallet[];
    solanaWallets: Wallet[];
    suiWallets: Wallet[];
    aptosWallets: Wallet[];
  }>(
    (acc, wallet) => {
      if (wallet.name.includes("Ethereum")) {
        // Filter out the auto-derived WalletConnect wallet — we show a custom WalletConnectEvmRow instead
        if (!wallet.name.includes("WalletConnect")) {
          acc.evmWallets.push(wallet);
        }
      } else if (wallet.name.includes("Solana")) {
        // Filter out the auto-derived WalletConnect wallet — we show a custom WalletConnectSolanaRow instead
        if (!wallet.name.includes("WalletConnect")) {
          acc.solanaWallets.push(wallet);
        }
      } else if (wallet.name.includes("Sui")) {
        acc.suiWallets.push(wallet);
      } else {
        acc.aptosWallets.push(wallet);
      }
      return acc;
    },
    { evmWallets: [], solanaWallets: [], suiWallets: [], aptosWallets: [] },
  );

  const {
    evmInstallableWallets,
    solanaInstallableWallets,
    suiInstallableWallets,
    aptosInstallableWallets,
  } = installableWallets.reduce<{
    evmInstallableWallets: AdapterNotDetectedWallet[];
    solanaInstallableWallets: AdapterNotDetectedWallet[];
    suiInstallableWallets: AdapterNotDetectedWallet[];
    aptosInstallableWallets: AdapterNotDetectedWallet[];
  }>(
    (acc, wallet) => {
      if (wallet.name.includes("Ethereum")) {
        acc.evmInstallableWallets.push(wallet);
      } else if (wallet.name.includes("Solana")) {
        acc.solanaInstallableWallets.push(wallet);
      } else if (wallet.name.includes("Sui")) {
        acc.suiInstallableWallets.push(wallet);
      } else {
        acc.aptosInstallableWallets.push(wallet);
      }
      return acc;
    },
    {
      evmInstallableWallets: [],
      solanaInstallableWallets: [],
      suiInstallableWallets: [],
      aptosInstallableWallets: [],
    },
  );

  const [activeTab, setActiveTab] = useState<SupportedNetworkId>(() =>
    isSupportedNetworkId(lastUsedTab)
      ? lastUsedTab
      : getDefaultTab(evmWallets, solanaWallets, suiWallets, aptosWallets),
  );

  const handleConnect = useCallback(
    (tab: SupportedNetworkId) => {
      setLastUsedTab(tab);
      close();
    },
    [close, setLastUsedTab],
  );

  const handleEvmChainSelect = useCallback(
    (chain: EvmChainId) => {
      setSelectedEvmChain(chain);
      setLastUsedEvmChain(chain);
    },
    [setLastUsedEvmChain, setSelectedEvmChain],
  );

  return (
    <ModalContent
      className={css({
        position: "relative",
        w: "[386px]",
        bg: "background.canvas",
        bgImage: "unset",
        p: "0",
      })}
    >
      <div
        className={css({
          backgroundImage: "url(/images/waves-bg.webp)",
          backgroundPosition: "top",
          backgroundRepeat: "no-repeat",
          backgroundSize: "100% auto",
          w: "full",
          p: "20",
        })}
      >
        <ModalCloseButton close={close} />

        <div
          className={stack({
            pt: "112",
            textAlign: "center",
            gap: "20",
            textStyle: "body.md",
            minHeight: "[458px]",
          })}
        >
          <div className={stack({ gap: "14" })}>
            {/* Connect to Decibel */}
            <h2 className={css({ textStyle: "highlight.xl" })}>{t("connectToDecibel")}</h2>
            {/* Text paragraph */}
            <p className={css({ textStyle: "body.md" })}>{t("selectWalletParagraph")}</p>
          </div>

          {!isCustomNetwork && (
            <div className={cx(keylessButtonGridStyles, css({ gridTemplateRows: "auto auto" }))}>
              <KeylessButton
                provider="google"
                disabled={!env.NEXT_PUBLIC_ENABLE_KEYLESS}
                className={css({ gridColumn: "1", gridRow: "1" })}
              />

              <KeylessButton
                provider="apple"
                disabled
                className={css({ gridColumn: "2", gridRow: "1" })}
              />

              <ScanQrButton
                onSuccess={close}
                className={css({ gridColumn: "1 / span 2", gridRow: "2" })}
              />
            </div>
          )}

          <RadioTabGroup
            value={activeTab}
            onChange={setActiveTab}
            options={supportedNetworks.map((network) => ({
              value: network.id,
              label: network.name,
            }))}
          />

          <Tabs
            hideTriggers
            value={activeTab}
            onChange={setActiveTab}
            tabs={[
              {
                id: "aptos",
                trigger: "Aptos",
                content: (
                  <WalletTabContent
                    tab="aptos"
                    wallets={aptosWallets}
                    installableWallets={aptosInstallableWallets}
                    onConnect={handleConnect}
                  />
                ),
              },
              {
                id: "solana",
                trigger: "Solana",
                content: (
                  <WalletTabContent
                    tab="solana"
                    wallets={solanaWallets}
                    installableWallets={solanaInstallableWallets}
                    afterWallets={<WalletConnectSolanaRow onConnect={handleConnect} />}
                    onConnect={handleConnect}
                  />
                ),
              },
              {
                id: "ethereum",
                trigger: "EVM",
                content: (
                  <WalletTabContent
                    tab="ethereum"
                    header={
                      <div className={stack({ gap: "8" })}>
                        <EVMChainSelectorMenu
                          selectedChain={selectedEvmChain}
                          onChainSelect={handleEvmChainSelect}
                        />
                        <p className={css({ textStyle: "body.sm", color: "text.tertiary" })}>
                          {t("chainSwitchDescription")}
                        </p>
                      </div>
                    }
                    wallets={evmWallets}
                    installableWallets={evmInstallableWallets}
                    afterWallets={<WalletConnectEvmRow onConnect={handleConnect} />}
                  />
                ),
              },
              {
                id: "sui" as const,
                trigger: "Sui",
                content: (
                  <WalletTabContent
                    tab="sui"
                    header={
                      <span className={css({ color: "text.tertiary", whiteSpace: "pre-wrap" })}>
                        {t("suiWarningHeader")}
                      </span>
                    }
                    wallets={suiWallets}
                    installableWallets={suiInstallableWallets}
                    onConnect={handleConnect}
                  />
                ),
              },
            ]}
          />
          <div className={stack({ gap: "8", align: "start", marginTop: "auto" })}>
            <p className={css({ textStyle: "body.md", fontSize: "14" })}>{t("dontHaveAWallet")}</p>
            <IconButton
              variant="secondary"
              size="md"
              ariaLabel={t("downloadPetraExtension")}
              onClick={() => openInNewTab("https://petra.app/")}
              className={css({ w: "full" })}
            >
              <div className={flex({ gap: "8", align: "center", justify: "center" })}>
                <PetraLogo className={css({ w: "16", h: "16" })} />
                {t("downloadPetraExtension")}
              </div>
            </IconButton>
          </div>
          <ReferralCodeSection />
        </div>
      </div>
    </ModalContent>
  );
}

function ReferralCodeSection() {
  const t = useTranslations("walletSelector.referralInput");
  const referralCodeT = useTranslations("referralCode");
  const generalT = useTranslations("general");
  const { accountAddress } = useDecibelWallet();

  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState("");
  const trimmedCode = code.trim();
  const debouncedCode = useDebounce(trimmedCode, 500);
  const codeValidation = useIsValidRefCodeQuery(debouncedCode || undefined);
  const { mutateAsync: submitInvitationCode, isPending } = useSubmitInvitationCode();

  const panelId = useId();
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const isDebouncing = trimmedCode !== debouncedCode;
  const validation = codeValidation.data;
  const isValidatedAndActive = validation?.is_valid && validation.is_active;
  const canSubmit =
    trimmedCode.length > 0 &&
    isValidatedAndActive &&
    !isDebouncing &&
    !codeValidation.isLoading &&
    !isPending;

  const handleSubmit = async () => {
    if (!trimmedCode) return;
    try {
      if (accountAddress) {
        await submitInvitationCode({ code: trimmedCode, account: accountAddress });
        // Clear any stale pending code from an earlier /r/<code> flow so
        // HandlePendingReferralCode doesn't try to redeem it on the next render.
        removePendingReferralCode();
        toast({
          variant: "success",
          title: generalT("success"),
          description: referralCodeT("codeRedeemed"),
        });
      } else {
        setPendingReferralCode(trimmedCode);
        toast({
          variant: "info",
          title: referralCodeT("codeApplied"),
          description: referralCodeT("connectToContinue"),
        });
      }
      if (!isMountedRef.current) return;
      setCode("");
      setExpanded(false);
    } catch (err) {
      toast({
        variant: "error",
        title: generalT("error"),
        description: getErrorMessage(err) || referralCodeT("somethingWentWrong"),
      });
    }
  };

  return (
    <div className={stack({ gap: "8", w: "full", align: "stretch" })}>
      <Clickable
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        aria-controls={panelId}
        className={referralToggleStyles}
      >
        <IconGift className={css({ w: "20", h: "20" })} />
        <span
          className={css({
            textStyle: "body.lg",
            textDecoration: "underline",
          })}
        >
          {t("haveAReferralCode")}
        </span>
      </Clickable>
      {expanded && (
        <div id={panelId} className={flex({ gap: "8", w: "full", align: "center" })}>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t("enterCode")}
            maxLength={REFERRAL_CODE_MAX_LENGTH}
            size="md"
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSubmit) void handleSubmit();
            }}
            tailElement={
              <CodeValidationStatus
                code={trimmedCode}
                debouncedCode={debouncedCode}
                codeValidation={codeValidation}
                t={referralCodeT}
              />
            }
            className={css({
              bg: "background.secondary!",
              border: "none!",
              px: "8!",
              h: "32",
              flex: "1",
              "& input": { textStyle: "body.lg!" },
            })}
          />
          <IconButton
            variant="secondary"
            size="md"
            ariaLabel={t("applyCode")}
            disabled={!canSubmit}
            loading={isPending}
            onClick={() => void handleSubmit()}
            className={css({ p: "[7px!]" })}
          >
            <IconCheck className={css({ w: "16", h: "16" })} />
          </IconButton>
        </div>
      )}
    </div>
  );
}

function CodeValidationStatus({
  code,
  debouncedCode,
  codeValidation,
  t,
}: {
  code: string;
  debouncedCode: string;
  codeValidation: UseQueryResult<ReferralCodeValidation>;
  t: ReturnType<typeof useTranslations<"referralCode">>;
}) {
  if (!code) return null;
  const isDebouncing = code !== debouncedCode;
  if (isDebouncing || codeValidation.isLoading) {
    return <Spinner size="xs" variant="secondary" />;
  }
  if (!codeValidation.data) return null;
  const { is_valid, is_active } = codeValidation.data;
  if (is_valid && is_active) {
    return (
      <Tooltip content={t("codeValid")}>
        <IconCheckCircle className={css({ color: "text.positive", w: "16", h: "16" })} />
      </Tooltip>
    );
  }
  return (
    <Tooltip content={is_valid ? t("codeExhausted") : t("codeInvalid")}>
      <IconXCircle className={css({ color: "text.negative", w: "16", h: "16" })} />
    </Tooltip>
  );
}

const referralToggleStyles = flex({
  gap: "4",
  align: "center",
  cursor: "pointer",
  color: "text.secondary",
  bg: "transparent",
  border: "none",
  p: "0",
  _hover: { color: "text.primary" },
});

const ScanQrButton = ({ onSuccess, className }: { onSuccess?: () => void; className?: string }) => {
  const t = useTranslations("authWindow");

  if (!isMobile()) return null;

  return (
    <KeylessButtonLayout
      LeadIcon={IconDesktopFill}
      shortLabel={t("linkDesktopWallet")}
      label={t("linkDesktopWallet")}
      onClick={() => openScanQrCodeModal({ onSuccess })}
      className={className}
    />
  );
};

const keylessButtonGridStyles = css({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gridTemplateRows: "auto auto",
  gap: "8",
  justifyItems: "center",
  alignItems: "center",
});
